import React from 'react';
import { Box, Text, Button, useColorMode } from '@chakra-ui/react';
import ThemePhoto, { PhotoWidths } from './ThemePhoto';
import './Pricing.css';

// ─── Ceník ────────────────────────────────────────────────────────────────
// Vše se dá upravit tady – ceny, názvy balíčků, popisky i fotky u témat.
//
// Řádek s cenou má dvě podoby:
//   • s "name"  → název balíčku + cena, popis se vypíše pod tím
//   • bez "name" → v doplňkových službách jednořádkový zápis, kde je vlevo
//     rovnou popis; v kartě s fotkou jen linka s cenou a popis pod ní (viz
//     PriceRows – dlouhý popis by se s cenou do karty nevešel)
//
// Podmínky společné pro všechna focení jsou schválně jen jednou v PRICING_NOTE
// pod tabulkou – jinak by se opakovaly a ceník by byl zbytečně dlouhý.
//
// "photo" je cesta k fotce bez přípony (jako v galeriích) – ve světlém režimu
// je barevná, v tmavém černobílá. Fotky se připravují v scripts/build-photos.mjs.
// Když je velká verze fotky užší než 1600 px, zapiš ji do "photoWidths"
// (jinak by si prohlížeč myslel, že je ostřejší, než je).

const PRICE_VALID_FROM = 'Ceník je platný od 1. 9. 2026';

const PRICING_NOTE =
  'Ke každému focení: fotografie v tiskové i webové kvalitě, vše převádím ' +
  'i do černobílé varianty, focení trvá 30–45 minut a hotové fotografie ' +
  'najdete v online galerii na cca 3 měsíce.';

interface PriceRow {
  name?: string;
  desc: string;
  price: string;
}

interface PriceGroup {
  title: string;
  photo?: string;
  photoWidths?: PhotoWidths;
  rows: PriceRow[];
}

// Jak široká je fotka v kartě (Pricing.css) – podle toho si prohlížeč vybere
// náhled, nebo velkou verzi: mobil kompaktní karta (fotka vlevo), pak dva
// sloupce v bloku 800 px, od 1280 px čtyři sloupce (~21 % šířky okna).
const CARD_PHOTO_SIZES =
  '(max-width: 480px) 150px, (max-width: 700px) 230px, (max-width: 1279px) 364px, 21vw';

// Rodinné a párové focení mají stejné dva balíčky
const STANDARD_ROWS: PriceRow[] = [
  {
    name: 'Klasické focení',
    desc: 'Minimálně 30 upravených fotografií, každá další + 100 Kč',
    price: '3.200 Kč',
  },
  {
    name: 'Fotopříběh',
    desc: 'Neomezený počet upravených fotografií',
    price: '4.000 Kč',
  },
];

// Hlavní témata focení – každé má svoji fotku
const THEME_GROUPS: PriceGroup[] = [
  {
    title: 'Rodinné focení',
    photo: '/photos/galerie/rodinne/07',
    // Originál byl menší – velká verze má jen 1365 px
    photoWidths: { thumb: 800, full: 1365 },
    rows: STANDARD_ROWS,
  },
  { title: 'Párové focení', photo: '/photos/galerie/parove/02', rows: STANDARD_ROWS },
  {
    title: 'Portrétní focení',
    photo: '/photos/galerie/portretni/02',
    rows: [{ desc: 'Minimálně 30 upravených fotografií', price: '4.000 Kč' }],
  },
  {
    title: 'Svatební focení',
    photo: '/photos/galerie/svatebni/01',
    rows: [
      {
        name: 'Ceník na vyžádání',
        desc: 'Nabídku připravím podle programu vašeho dne',
        price: 'od 18.000 Kč',
      },
    ],
  },
];

// Doplňkové služby – bez fotky, drží se strohého zápisu
const EXTRA_GROUPS: PriceGroup[] = [
  {
    title: 'Produktové a brandové focení',
    rows: [{ desc: 'Cena podle rozsahu a domluvy', price: 'Individuální' }],
  },
  {
    title: 'Dárkový poukaz',
    rows: [{ desc: 'Poukaz na libovolný typ focení', price: 'Individuální' }],
  },
];

// ─── Jeden řádek s cenou ────────────────────────────────────────────────────
interface PriceRowsProps {
  rows: PriceRow[];
  textColor: string;
  subTextColor: string;
  /**
   * Řádek bez názvu jako linka s cenou a popis pod ní. V kartě s fotkou je to
   * nutné: „Minimálně 30 upravených fotografií“ se s cenou na jeden řádek
   * nevejde skoro na žádné šířce (ve 4 sloupcích až od ~2150 px) a cena by
   * skákala pod popis – níž než v ostatních kartách.
   */
  descBelow?: boolean;
}

const PriceRows: React.FC<PriceRowsProps> = ({ rows, textColor, subTextColor, descBelow = false }) => (
  <>
    {rows.map((row) => {
      const label = row.name ?? (descBelow ? undefined : row.desc);
      const showDesc = Boolean(row.name) || descBelow;
      return (
        <Box key={row.price + row.desc} className="price-row">
          <Box className="price-row-head">
            {label && (
              <Text as="span" className="price-row-name" color={textColor}>
                {label}
              </Text>
            )}
            {/* Linka a cena drží spolu – v úzké kartě se zalomí jako celek pod
                název; bez názvu vede linka od kraje karty */}
            <Box className="price-row-value">
              <Box className="price-row-rule" />
              <Text as="span" className="price-row-price" color={textColor}>
                {row.price}
              </Text>
            </Box>
          </Box>
          {showDesc && (
            <Text className="price-row-desc" color={subTextColor}>
              {row.desc}
            </Text>
          )}
        </Box>
      );
    })}
  </>
);

const Pricing: React.FC = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const textColor = isDark ? 'white' : 'black';
  const subTextColor = isDark ? 'gray.300' : '#555';

  const handleScrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id="pricing"
      as="section"
      className="pricing-container"
      bg={isDark ? 'gray.800' : '#f8f8f8'}
    >
      <Box className="pricing-header reveal">
        <Text className="pricing-title" color={textColor}>
          Ceník
        </Text>
        <Text className="pricing-subtitle" color={subTextColor}>
          Investice do vzpomínek, které nevyblednou
        </Text>
        <Text className="pricing-validity" color={subTextColor}>
          {PRICE_VALID_FROM}
        </Text>
      </Box>

      {/* Hlavní témata – karta s fotkou a cenami */}
      <Box className="pricing-cards">
        {THEME_GROUPS.map((group) => (
          <Box key={group.title} className="price-card reveal">
            <Box className="price-card-media">
              <ThemePhoto
                base={group.photo!}
                alt={group.title}
                thumb
                sizes={CARD_PHOTO_SIZES}
                widths={group.photoWidths}
                className="price-card-photo"
              />
            </Box>
            <Box className="price-card-body">
              <Text className="price-card-title" color={textColor}>
                {group.title}
              </Text>
              <PriceRows
                rows={group.rows}
                textColor={textColor}
                subTextColor={subTextColor}
                descBelow
              />
            </Box>
          </Box>
        ))}
      </Box>

      {/* Doplňkové služby – bez fotek */}
      <Box className="pricing-extras reveal">
        {EXTRA_GROUPS.map((group) => (
          <Box key={group.title} className="price-extra">
            <Text className="price-group-title" color={subTextColor}>
              {group.title}
            </Text>
            <PriceRows rows={group.rows} textColor={textColor} subTextColor={subTextColor} />
          </Box>
        ))}
      </Box>

      <Text className="pricing-note reveal" color={subTextColor}>
        {PRICING_NOTE}
      </Text>

      <Button className="pricing-button reveal" onClick={handleScrollToContact}>
        Nezávazně poptat
      </Button>
    </Box>
  );
};

export default Pricing;
